import { createDateRangeFilter, createEntityLookupFilter } from './factories';
import type { FilterRegistry } from './types';

/**
 * Filters for the stored-statements list (PD-14042). The statements list
 * endpoint takes a NARROW query (clientId / companyId / asOfDateStart /
 * asOfDateEnd) rather than the generic list-builder filter payload, so the
 * statements module's queryFn translates these filter values into those
 * params client-side — `joinType: 'custom'` declares that no shared SQL
 * mapping applies.
 */
export const statementFilters: FilterRegistry = {
	statementClient: {
		...createEntityLookupFilter({
			label: 'Customer',
			placeholder: 'Search customers...',
			apiEndpoint: '/clients',
			objectTable: 'statement',
			fieldName: 'fk_client__id',
			listKeys: ['statement'],
			displayFields: {
				id: 'id',
				primary: 'name',
				secondary: 'externalId',
			},
			colorScheme: 'green',
			joinType: 'custom',
		}),
		searchFilterName: 'clientSearch',
		apiParams: {
			page: '1',
			limit: '10',
			fields: JSON.stringify(['client.id', 'client.name', 'client.externalId']),
		},
		queryParamName: 'statementClient',
		valueExtractor: {
			property: 'id',
		},
	},

	statementCompany: {
		...createEntityLookupFilter({
			label: 'Company',
			placeholder: 'Search companies...',
			apiEndpoint: '/users/companies',
			objectTable: 'statement',
			fieldName: 'fk_company__id',
			listKeys: ['statement'],
			displayFields: {
				id: 'id',
				primary: 'label',
			},
			colorScheme: 'purple',
			joinType: 'custom',
		}),
		queryParamName: 'statementCompany',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
	},

	/**
	 * PD-15467 — same shape as `invoiceSalesRep`: "Me" is the only option a
	 * rep without statements:view:any ever sees (the list config locks and
	 * hides it), while a view:any user can pick any rep. Options come from the
	 * statements endpoint so a statements-only role can load them.
	 */
	statementSalesRep: {
		type: 'multi-select',
		label: 'Sales Rep',
		placeholder: 'Select sales representatives...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
		},
		apiEndpoint: '/statements/salesrep/options',
		options: [{ id: 'ME', label: 'Me' }],
		queryParamName: 'statementSalesRep',
		multiple: true,
		queryMapping: {
			objectTable: 'statement',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
		listKeys: ['statement'],
	},

	statementAsOfDateRange: {
		...createDateRangeFilter({
			label: 'As-of Date',
			objectTable: 'statement',
			dateField: 'asOfDate',
			listKeys: ['statement'],
		}),
		queryParamName: 'statementAsOfDateRange',
	},
};
